import React, { useCallback, useEffect, useRef, useState } from "react";
import { LngLatBounds } from "maplibre-gl";
import { Map, MapMarker, MapRoute, MarkerContent, useMap } from "./ui/map";

const OSRM_BASE = "https://router.project-osrm.org/route/v1/driving";

// [lon, lat]
const START: [number, number] = [11.937506, 45.570832];
const END: [number, number] = [12.2630107, 45.6190753];

function midpoint(a: [number, number], b: [number, number]): [number, number] {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

function RouteFitter({ coords }: { coords: [number, number][] }) {
  const { map, isLoaded } = useMap();
  const prevRef = useRef<[number, number][]>([]);

  useEffect(() => {
    if (!map || !isLoaded || coords.length < 2) return;
    if (coords === prevRef.current) return;
    prevRef.current = coords;
    const bounds = new LngLatBounds(coords[0], coords[0]);
    coords.forEach((c) => bounds.extend(c));
    map.fitBounds(bounds, { padding: 60, duration: 800 });
  }, [map, isLoaded, coords]);

  return null;
}

const TECH = [
  { name: "MapCN", desc: "Componenti React per mappe, basati su MapLibre GL" },
  { name: "MapLibre GL", desc: "Rendering vettoriale WebGL con tile Carto Dark" },
  { name: "OSRM", desc: "Open Source Routing Machine — calcolo percorso stradale" },
  { name: "GeoJSON", desc: "Formato dati per la geometria del percorso" },
];

export default function MapRouteSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const animTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTriggeredRef = useRef(false);

  const [info, setInfo] = useState<{ distance: number; duration: number } | null>(null);
  const [fullCoords, setFullCoords] = useState<[number, number][]>([]);
  const [animatedCoords, setAnimatedCoords] = useState<[number, number][]>([]);
  const [fitCoords, setFitCoords] = useState<[number, number][]>([]);

  useEffect(() => {
    return () => {
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
    };
  }, []);

  const calculateRoute = useCallback(async () => {
    if (animTimerRef.current) clearTimeout(animTimerRef.current);

    setInfo(null);
    setFullCoords([]);
    setAnimatedCoords([]);

    try {
      const url = `${OSRM_BASE}/${START[0]},${START[1]};${END[0]},${END[1]}?overview=full&geometries=geojson`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.code !== "Ok" || !data.routes?.length) return;

      const route = data.routes[0];
      const coords: [number, number][] = route.geometry.coordinates;
      const distKm = Math.round(route.distance / 100) / 10;
      const durMin = Math.round(route.duration / 60);

      setInfo({ distance: distKm, duration: durMin });
      setFullCoords(coords);
      setFitCoords(coords);

      let idx = 0;
      const TICK_MS = 16;
      const stepsPerTick = Math.max(1, Math.ceil(coords.length / (3000 / TICK_MS)));
      const tick = () => {
        idx = Math.min(idx + stepsPerTick, coords.length);
        setAnimatedCoords(coords.slice(0, idx));
        if (idx < coords.length) {
          animTimerRef.current = setTimeout(tick, TICK_MS);
        }
      };
      animTimerRef.current = setTimeout(tick, TICK_MS);
    } catch {
      // silently fail
    }
  }, []);

  // Avvia l'animazione automaticamente quando la sezione entra in viewport
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const root = document.getElementById("app-scroll");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTriggeredRef.current) {
            hasTriggeredRef.current = true;
            calculateRoute();
          }
          if (!entry.isIntersecting) {
            hasTriggeredRef.current = false;
          }
        });
      },
      { root, threshold: 0.3 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [calculateRoute]);

  return (
    <section ref={sectionRef} className="test-snap-section flex min-h-[100dvh] flex-col justify-center py-12">
      <div className="flex flex-col gap-6 md:h-[60vh] md:flex-row md:gap-8 md:px-8">
        {/* Left — description */}
        <div
          className="flex flex-col justify-center gap-6 rounded-2xl px-8 py-10 md:w-3/5"
          style={{ background: "linear-gradient(160deg, #0d1b3e 0%, #1a1040 100%)" }}
        >
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-indigo-400">
              Componente interattivo
            </p>
            <h2 className="text-3xl font-bold leading-tight text-white md:text-4xl">
              Percorso animato su mappa
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-zinc-400 md:text-base">
              Visualizzazione di un itinerario stradale tra due punti nel Veneto. Il percorso
              viene calcolato in tempo reale e disegnato progressivamente sulla mappa nel momento
              in cui la sezione diventa visibile.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
              Stack tecnologico
            </p>
            <ul className="space-y-2.5">
              {TECH.map((t) => (
                <li key={t.name} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-400" />
                  <span className="text-sm text-zinc-300">
                    <span className="font-semibold text-white">{t.name}</span>
                    {" — "}
                    <span className="text-zinc-400">{t.desc}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
              Come funziona
            </p>
            <p className="text-sm leading-relaxed text-zinc-400">
              Una richiesta all'API OSRM restituisce la geometria GeoJSON del percorso stradale.
              Le coordinate vengono poi disegnate sulla mappa con due layer sovrapposti: uno sfondo
              bianco semitrasparente che mostra il tracciato completo, e una linea ciano animata
              che si disegna progressivamente in massimo 3 secondi.
            </p>
          </div>

          {info && (
            <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white w-fit">
              <span className="font-bold">{info.distance} km</span>
              <span className="mx-1.5 opacity-40">·</span>
              <span>{info.duration} min</span>
            </div>
          )}
        </div>

        {/* Right — map */}
        <div className="relative h-[60vh] overflow-hidden rounded-2xl md:h-full md:flex-1">
          <Map
            theme="dark"
            className="h-full w-full"
            center={midpoint(START, END)}
            zoom={10}
          >
            <RouteFitter coords={fitCoords} />

            {fullCoords.length > 1 && (
              <MapRoute
                id="route-bg"
                coordinates={fullCoords}
                color="#ffffff"
                width={7}
                opacity={0.25}
                interactive={false}
              />
            )}

            {animatedCoords.length > 1 && (
              <MapRoute
                id="route-fg"
                coordinates={animatedCoords}
                color="#00c8ff"
                width={5}
                opacity={1}
                interactive={false}
              />
            )}

            <MapMarker longitude={START[0]} latitude={START[1]}>
              <MarkerContent>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "#3887be",
                    border: "2.5px solid white",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
                  }}
                />
              </MarkerContent>
            </MapMarker>

            <MapMarker longitude={END[0]} latitude={END[1]}>
              <MarkerContent>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "#ff3300",
                    border: "2.5px solid white",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
                  }}
                />
              </MarkerContent>
            </MapMarker>
          </Map>
        </div>
      </div>
    </section>
  );
}
